import { useState, useEffect } from "react";
import { Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ja';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import vacationImage from '../../assets/vacation.png';
import { supabase } from "../../configs/supabase";

dayjs.locale('ja');

function VacationDay(props: PickersDayProps<Dayjs> & { vacationDates?: Dayjs[], onDayClick: (day: Dayjs) => void }) {
  const { vacationDates = [], day, outsideCurrentMonth, onDayClick, ...other } = props;

  const isVacation = !outsideCurrentMonth && vacationDates.some(vacationDate => vacationDate.isSame(day, 'day'));

  return (
    <div style={{ position: 'relative' }} onClick={() => onDayClick(day)}>
      <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
      {isVacation && (
        <img
          src={vacationImage}
          alt="Vacation"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '14px',
            height: '14px',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

export default function CalendarSetting() {
  const [vacations, setVacations] = useState<Dayjs[]>([]);
  const vacationDates = vacations.map(vacation => dayjs(vacation));
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchVacations = async () => {
      const { data, error } = await supabase.from('vacation').select('vacation_date');
      if (error) {
        console.error('Error fetching vacation dates:', error);
      } else {
        setVacations(data.map((item: { vacation_date: string }) => dayjs(item.vacation_date)));
      }
    };

    fetchVacations();
  }, []);

  const handleDayClick = (day: Dayjs) => {
    setSelectedDate(day);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (selectedDate) {
      const isVacation = vacationDates.some(vacationDate => vacationDate.isSame(selectedDate, 'day'));

      if (isVacation) {
        // 休みを解除
        const { error } = await supabase
          .from('vacation')
          .delete()
          .eq('vacation_date', selectedDate.format('YYYY-MM-DD'));

        if (error) {
          console.error('Error removing vacation date:', error);
        }
      } else {
        // 休みを登録
        const { error } = await supabase
          .from('vacation')
          .insert([{ vacation_date: selectedDate.format('YYYY-MM-DD') }]);

        if (error) {
          console.error('Error adding vacation date:', error);
        }
      }

      // データを再取得
      const { data, error: fetchError } = await supabase.from('vacation').select('vacation_date');
      if (fetchError) {
        console.error('Error fetching vacation dates:', fetchError);
      } else {
        setVacations(data.map((item: { vacation_date: string }) => dayjs(item.vacation_date)));
      }
    }
    setOpen(false);
  };

  return (
    <Container>
      <h2>カレンダー設定</h2>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
        <DateCalendar
          slots={{
            day: (props) => <VacationDay {...props} onDayClick={handleDayClick} />,
          }}
          slotProps={{
            day: {
              vacationDates,
            } as any,
          }}
        />
      </LocalizationProvider>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>休みの設定</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedDate && vacationDates.some(vacationDate => vacationDate.isSame(selectedDate, 'day'))
              ? "休み登録を解除しますか？"
              : "休み登録しますか？"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            いいえ
          </Button>
          <Button onClick={handleConfirm} color="primary" autoFocus>
            はい
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
